import React, { useEffect, useState } from 'react';
import { styles } from './UnverifiedSignupPrompt.css';
import { SocialLogins } from '../../../loginCreate/socialLogins/SocialLogins';
import { handleLoginCreate } from '../../../../../utils/signInOutUp/AuthenticationHelper';
import { isEmailValid } from '../../../../../utils/common/validateEmail/validateEmail';
import { trackingContext, formAction, useTracking } from '@jbhi-fi/components/src/Tracking';
import Button from '@jbhi-fi/lanyard-ui/components/Button/Button';

const EMAIL_ERROR_MESSAGE = "Your email address doesn't look right. Please try again.";

export type UnverifiedSignupPromptPropsType = {
  returnUrl?: string;
  customerEmail?: string;
  isInline?: boolean;
  gtmContext?: trackingContext;
};

export const UnverifiedSignupPrompt = ({
  customerEmail = '',
  returnUrl = '/account/login?pageTheme=default',
  isInline = false,
  gtmContext = trackingContext.account,
}: UnverifiedSignupPromptPropsType): JSX.Element => {
  const [email, setEmail] = useState(customerEmail);
  const [emailHasError, setEmailHasError] = useState(false);
  const [termsPolicyChecked, setTermsPolicyChecked] = useState(false);
  const fromReceiptsMicrosite = gtmContext === trackingContext.receipts;
  const { tracker } = useTracking({
    context: gtmContext,
    category: fromReceiptsMicrosite ? 'eReceipts - Unverified' : 'my account - Unverified',
    group: 'landing',
  });

  useEffect(() => {
    if (window.Jbhifi.isPerksDisabled) {
      setTermsPolicyChecked(true);
    }
    tracker.trackFormEvent({ action: formAction.stepView });
  }, []);

  const handleSocialRegistration = (domainHint: string) => {
    handleLoginCreate(
      returnUrl,
      {
        domainHint,
        userJourneyMode: 'EXTERNAL_ACCOUNT',
        pageTheme: 'default',
      },
      { fromReceiptsMicrosite }
    );
  };

  const handleEmailRegistration = (): void => {
    handleLoginCreate(
      returnUrl,
      {
        loginHint: email,
        userJourneyMode: 'LOCAL_ACCOUNT_REGISTRATION',
        pageTheme: 'default',
      },
      { fromReceiptsMicrosite }
    );
  };

  const handleClick = (event: React.FormEvent, domainHint?: string): void => {
    event.preventDefault();

    if (!domainHint && !isEmailValid(email)) {
      if (!domainHint && !isEmailValid(email)) {
        setEmailHasError(true);
        tracker.trackError({ errorMsg: EMAIL_ERROR_MESSAGE, contextOverride: trackingContext.form });
      }

      return;
    }
    tracker.trackClick({ label: domainHint?.split('.')?.[0] ?? 'Continue' });
    domainHint ? handleSocialRegistration(domainHint) : handleEmailRegistration();
  };

  useEffect(() => {
    if (emailHasError) setEmailHasError(false);
  }, [email]);

  return (
    <form onSubmit={handleClick} className={styles.container}>
      <div className={styles.defaultLoginButtons}>
        <SocialLogins handleClick={handleClick} heading={window.Jbhifi.receiptDisclaimerDefault} />
      </div>
      <div className={styles.linkContainer}>
        or{' '}
        <Button
          type="link"
          onClick={handleEmailRegistration}
          label="create an account using email"
        ></Button>
      </div>
    </form>
  );
};
